import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import {
  Check as FiCheck,
  X as FiX,
  Eye as FiEye,
  Trash3 as FiTrash2,
  ThreeDotsVertical as FiMoreVertical,
} from 'react-bootstrap-icons';
import type { Return } from '../../api/returns';
import styles from './Returns.module.scss';

const returnActionsPopperConfig = {
  strategy: 'fixed' as const,
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        altBoundary: true,
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-end', 'top-start', 'bottom-start'],
      },
    },
  ],
};

export interface ReturnRowActionsProps {
  returnObj: Return;
  returnApprovalRequired: boolean;
  layout?: 'table' | 'mobile';
  /** When false, hide mutate actions (approve/reject/delete). */
  canWrite?: boolean;
  onView: (returnObj: Return) => void;
  onApprove: (returnObj: Return) => void;
  onReject: (returnObj: Return) => void;
  onDelete: (returnObj: Return) => void;
}

const ReturnRowActions: React.FC<ReturnRowActionsProps> = ({
  returnObj,
  returnApprovalRequired,
  layout = 'table',
  canWrite = true,
  onView,
  onApprove,
  onReject,
  onDelete,
}) => {
  const isTable = layout === 'table';
  const primaryBtnClass = isTable ? styles.actionBtn : 'flex-fill';
  const labeled = !isTable;
  const approveDisabled = returnObj.approvalState === 'approved';
  const rejectDisabled = returnObj.approvalState !== 'pending';
  const showApprovalActions = canWrite && returnApprovalRequired;

  return (
    <div
      className={isTable ? styles.actionsGroup : 'd-flex flex-wrap gap-1 w-100'}
      onClick={e => e.stopPropagation()}
    >
      {showApprovalActions && (
        <>
          <Button
            variant="outline-success"
            size="sm"
            disabled={approveDisabled}
            onClick={() => onApprove(returnObj)}
            title="Aprobar devolución"
            className={primaryBtnClass}
          >
            <FiCheck className={labeled ? 'me-1' : undefined} />
            {labeled ? 'Aprobar' : null}
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            disabled={rejectDisabled}
            onClick={() => onReject(returnObj)}
            title="Rechazar devolución"
            className={primaryBtnClass}
          >
            <FiX className={labeled ? 'me-1' : undefined} />
            {labeled ? 'Rechazar' : null}
          </Button>
        </>
      )}
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => onView(returnObj)}
        title="Ver detalles"
        className={primaryBtnClass}
      >
        <FiEye className={labeled ? 'me-1' : undefined} />
        {labeled ? 'Ver' : null}
      </Button>

      {canWrite ? (
        <Dropdown
          align="end"
          className={styles.actionsDropdown}
          onClick={e => e.stopPropagation()}
        >
          <Dropdown.Toggle
            variant="outline-secondary"
            size="sm"
            id={`return-actions-${returnObj.id}`}
            className={`${styles.actionBtn} ${styles.moreActionsToggle}`}
            title="Más acciones"
          >
            <FiMoreVertical />
          </Dropdown.Toggle>
          <Dropdown.Menu popperConfig={returnActionsPopperConfig} renderOnMount>
            <Dropdown.Item
              className="text-danger"
              onClick={() => onDelete(returnObj)}
            >
              <FiTrash2 className="me-2" />
              Mover a papelera
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      ) : null}
    </div>
  );
};

export default ReturnRowActions;
